import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig.js";
import { toast } from "react-toastify";
import "./CoachForm.css";
import AvailableTimesDialog from "./schedule/AvailableTimesDialog.js";
import LoginHistoryModal from "./LoginHistoryModal.js";
import { API_URL } from "../config/api.js";

const CoachManagement = () => {
  const [coaches, setCoaches] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [editingAvailableTimes, setEditingAvailableTimes] = useState(false);
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null); // { coach, studentCount }
  const [editedCoach, setEditedCoach] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    birthday: "",
    adress: "",
    comment: String, // Notiz
    availableTimes: [String], // Liste mit erlaubten Zeiten
    isCoachingAdult: Boolean, // Ist der Coach für Erwachsenen-Training zuständig
    isCoachingChildren: Boolean, // Ist der Coach für Kinder-Training zuständig
    CoachingAdultLevels: [],
    CoachingChildrenLevels: [],
  });

  useEffect(() => {
    fetchCoaches();
    fetchUsers();
  }, []);

  // Restore selected coach from localStorage on mount
  useEffect(() => {
    if (coaches.length > 0) {
      const savedCoachId = localStorage.getItem("selectedCoachId");
      if (savedCoachId) {
        const coach = coaches.find((c) => c._id === savedCoachId);
        if (coach) {
          setSelectedCoach(coach._id);
          setEditedCoach({ ...coach });
        } else {
          // Coach was deleted, silently clear
          localStorage.removeItem("selectedCoachId");
        }
      }
    }
  }, [coaches]);

  const fetchCoaches = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/coaches`);
      setCoaches(res.data);
    } catch (err) {
      console.error("Fehler beim Abrufen der Coaches:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`);
      setUsers(res.data);
    } catch (err) {
      console.error("Fehler beim Abrufen der Benutzer:", err);
    }
  };

  // Helper function to find user linked to selected coach
  const getLinkedUser = () => {
    if (!selectedCoach) return null;

    const linkedUser = users.find(
      (u) => u.coachId && String(u.coachId) === String(selectedCoach)
    );

    return linkedUser || null;
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSelectCoach = (coach) => {
    setSelectedCoach(coach._id);
    setEditedCoach({
      ...coach,
    });
    // Save selected coach to localStorage
    localStorage.setItem("selectedCoachId", coach._id);
  };

  const handleSaveAvailableTimes = (selectedTimes) => {
    setEditedCoach((prev) => ({
      ...prev,
      availableTimes: selectedTimes,
    }));

    // Optional: API-Aufruf, um die Daten zu speichern
    /*axios
      .put(`${API_URL}/api/students/${studentId}`, {
        availableTimes: selectedTimes,
      })
      .catch((err) =>
        console.error("Fehler beim Speichern der Trainingszeiten:", err)
      );*/

    setEditingAvailableTimes(false); // Dialog schließen
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditedCoach((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API_URL}/api/coaches/${selectedCoach}`, {
        ...editedCoach,
        availableTimes: editedCoach.availableTimes,
      });
      fetchCoaches();
      toast.success("Trainer erfolgreich aktualisiert!");
      setSelectedCoach(null);
      // Clear from localStorage after save
      localStorage.removeItem("selectedCoachId");
    } catch (err) {
      console.error("Fehler beim Speichern:", err);
      const errorMessage =
        err.response?.data?.error || "Fehler beim Speichern der Änderungen";
      toast.error(errorMessage);
    }
  };

  // Handle Enter key press to save
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Prevent default only if not in a textarea or specific elements
      if (e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'BUTTON') {
        e.preventDefault();
        handleSave();
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      // Find the coach to get firstName and lastName
      const coach = coaches.find((c) => c._id === id);
      if (!coach) {
        toast.error("Coach nicht gefunden");
        return;
      }

      // Fetch all students to check assignments
      const studentsResponse = await axios.get(`${API_URL}/api/students`);
      const allStudents = studentsResponse.data;

      // Count students assigned to this coach
      // Check both legacy format (student.coach) and new format (student.assignments[])
      const assignedStudents = allStudents.filter((student) => {
        // Check legacy format
        if (student.coach && String(student.coach) === String(id)) {
          return true;
        }
        // Check new format (assignments array)
        if (student.assignments && student.assignments.length > 0) {
          return student.assignments.some(
            (assignment) =>
              assignment.coach && String(assignment.coach) === String(id)
          );
        }
        return false;
      });

      const count = assignedStudents.length;

      // Show confirmation modal
      setDeleteConfirmation({
        coach: coach,
        studentCount: count,
        assignedStudents: assignedStudents,
      });
    } catch (err) {
      console.error("Fehler beim Abrufen der Schülerdaten:", err);
      toast.error("Fehler beim Überprüfen der Schülerzuweisungen");
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation) return;

    const { coach, studentCount, assignedStudents } = deleteConfirmation;

    try {
      // Unassign all students from this coach
      if (studentCount > 0) {
        for (const student of assignedStudents) {
          // Check if student has multiple assignments
          if (student.assignments && student.assignments.length > 0) {
            // Filter out assignments with this coach
            // Normalize coach IDs for comparison (handle null/undefined)
            const remainingAssignments = student.assignments.filter(
              (assignment) => {
                if (!assignment.coach) return true; // Keep assignments without coach
                const assignmentCoachId = String(assignment.coach).trim();
                const deletedCoachId = String(coach._id).trim();
                return assignmentCoachId !== deletedCoachId;
              }
            );

            console.log(`Student ${student.firstName} ${student.lastName}:`);
            console.log(`  Original assignments: ${student.assignments.length}`);
            console.log(`  Remaining assignments: ${remainingAssignments.length}`);

            // Update legacy fields with first remaining assignment (if any)
            let updatedDay = null;
            let updatedHour = null;
            let updatedCoach = null;

            if (remainingAssignments.length > 0) {
              updatedDay = remainingAssignments[0].day;
              updatedHour = remainingAssignments[0].hour;
              updatedCoach = remainingAssignments[0].coach;
            }

            // Update student with remaining assignments
            await axios.put(`${API_URL}/api/students/${student._id}`, {
              ...student,
              assignments: remainingAssignments,
              day: updatedDay,
              hour: updatedHour,
              coach: updatedCoach,
            });
          } else {
            // Legacy format: clear all assignment fields
            await axios.put(`${API_URL}/api/students/${student._id}`, {
              ...student,
              day: null,
              hour: null,
              coach: null,
              assignments: [],
            });
          }
        }

        toast.success(
          `${studentCount} Schüler wurde${
            studentCount === 1 ? "" : "n"
          } vom Trainer entfernt`
        );
      }

      // Now delete the coach
      await axios.delete(`${API_URL}/api/coaches/${coach._id}`);
      fetchCoaches();
      toast.success("Trainer erfolgreich gelöscht");

      // Clear confirmation modal
      setDeleteConfirmation(null);

      // Clear selection if the deleted coach was selected
      if (selectedCoach === coach._id) {
        setSelectedCoach(null);
        setEditedCoach({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          birthday: "",
          adress: "",
          comment: "",
          availableTimes: [],
          isCoachingAdult: false,
          isCoachingChildren: false,
          CoachingAdultLevels: [],
          CoachingChildrenLevels: [],
        });
        // Clear from localStorage
        localStorage.removeItem("selectedCoachId");
      }
    } catch (err) {
      console.error("Fehler beim Löschen:", err);
      toast.error("Fehler beim Löschen des Trainers");
    }
  };

  const filteredCoaches = coaches.filter((coach) =>
    `${coach.firstName} ${coach.lastName}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  return (
    <div className="student-form-container">
      <div className="student-form-header">
        <h1>🎾 Trainerverwaltung</h1>
      </div>

      <div className="student-form-content">
        <div className="student-list-card">
          <h2>Trainerliste</h2>
          <input
            type="text"
            className="search-box"
            placeholder="🔍 Trainer suchen..."
            value={searchQuery}
            onChange={handleSearch}
          />
          <p
            style={{ textAlign: "center", color: "#666", marginBottom: "15px" }}
          >
            <strong>{coaches.length}</strong> Trainer gespeichert
          </p>

          <div className="student-list">
            {filteredCoaches.map((coach) => (
              <div
                className={`student-item ${
                  selectedCoach?._id === coach._id ? "selected" : ""
                }`}
                key={coach._id}
                onClick={() => handleSelectCoach(coach)}
              >
                <span className="student-name">
                  {coach.firstName} {coach.lastName}
                </span>
                <button
                  className="delete-button"
                  title="Löschen"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(coach._id);
                  }}
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        </div>

        {selectedCoach && (
          <div className="edit-form-card" onKeyDown={handleKeyDown}>
            <h3 className="form-heading">Coach bearbeiten</h3>
            {/* Dialog zum Bearbeiten der Trainingszeiten */}
            {editingAvailableTimes && (
              <AvailableTimesDialog
                entity={editedCoach}
                closeDialog={() => setEditingAvailableTimes(false)}
                handleSaveAvailableTimes={handleSaveAvailableTimes}
              />
            )}
            {/* Labels über den Eingabefeldern */}
            <div className="form-field">
              <label htmlFor="firstName">Vorname</label>
              <input
                autoFocus
                id="firstName"
                type="text"
                name="firstName"
                value={editedCoach.firstName || ""}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-field">
              <label htmlFor="lastName">Nachname</label>
              <input
                id="lastName"
                type="text"
                name="lastName"
                value={editedCoach.lastName || ""}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-field">
              <label htmlFor="availableTimes">Verfügbare Zeiten</label>
              <button onClick={() => setEditingAvailableTimes(true)}>
                Trainingszeiten bearbeiten
              </button>
            </div>
            <div className="form-field">
              <label htmlFor="isCoachingAdult">Erwachsenen-Training</label>
              {/* Toggle-Switch statt Radiobuttons: 
                - Checkbox verwaltet Boolean direkt 
                - Bei checked: "Ja", sonst "Nein" */}
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="isCoachingAdult"
                  name="isCoachingAdult"
                  checked={editedCoach.isCoachingAdult}
                  onChange={(e) =>
                    setEditedCoach((prev) => ({
                      ...prev,
                      isCoachingAdult: e.target.checked,
                    }))
                  }
                />
                <span className="toggle-label">
                  {editedCoach.isCoachingAdult ? "Ja" : "Nein"}
                </span>
              </div>
            </div>
            <div className="form-field">
              <label htmlFor="isCoachingChildren">Jugend-Training</label>
              {/* Toggle-Switch statt Radiobuttons: 
                - Checkbox verwaltet Boolean direkt 
                - Bei checked: "Ja", sonst "Nein" */}
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="isCoachingChildren"
                  name="isCoachingChildren"
                  checked={editedCoach.isCoachingChildren}
                  onChange={(e) =>
                    setEditedCoach((prev) => ({
                      ...prev,
                      isCoachingChildren: e.target.checked,
                    }))
                  }
                />
                <span className="toggle-label">
                  {editedCoach.isCoachingChildren ? "Ja" : "Nein"}
                </span>
              </div>
            </div>
            {editedCoach.isCoachingChildren && (
              <div
                className="form-field"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  marginBottom: "20px",
                }}
              >
                {/* Feldlabel */}
                <label style={{ marginBottom: "0.5rem" }}>Kinder-Level</label>

                {/* Buttons „Alle auswählen“ / „Auswahl aufheben“ */}
                <div
                  style={{ margin: "0.5rem 0", display: "flex", gap: "0.5rem" }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setEditedCoach((prev) => ({
                        ...prev,
                        CoachingChildrenLevels: [
                          "Kinderland",
                          "Rot",
                          "Orange",
                          "Grün",
                          "Gelb Hobby",
                          "Gelb Team",
                        ],
                      }))
                    }
                    style={{
                      padding: "4px 8px",
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                  >
                    Alle auswählen
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setEditedCoach((prev) => ({
                        ...prev,
                        CoachingChildrenLevels: [],
                      }))
                    }
                    style={{
                      padding: "4px 8px",
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                  >
                    Auswahl aufheben
                  </button>
                </div>

                {/* Checkboxen in zwei Zeilen (3 Spalten) */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "0.5rem 1rem",
                    alignItems: "center",
                  }}
                >
                  {[
                    "Kinderland",
                    "Rot",
                    "Orange",
                    "Grün",
                    "Gelb Hobby",
                    "Gelb Team",
                  ].map((level) => (
                    <label
                      key={level}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <input
                        type="checkbox"
                        value={level}
                        checked={editedCoach.CoachingChildrenLevels.includes(
                          level
                        )}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setEditedCoach((prev) => ({
                            ...prev,
                            CoachingChildrenLevels: checked
                              ? [...prev.CoachingChildrenLevels, level]
                              : prev.CoachingChildrenLevels.filter(
                                  (l) => l !== level
                                ),
                          }));
                        }}
                        style={{ marginRight: "6px" }}
                      />
                      {level}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="form-field">
              <label htmlFor="email">E-Mail</label>
              <input
                id="email"
                type="text"
                name="email"
                value={editedCoach.email || ""}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-field">
              <label htmlFor="phone">Telefonnummer</label>
              <input
                id="phone"
                type="text"
                name="phone"
                value={editedCoach.phone || ""}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-field">
              <label htmlFor="birthday">Geburtsdatum</label>
              <input
                id="birthday"
                type="date"
                name="birthday"
                value={editedCoach.birthday || ""}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-field">
              <label htmlFor="adress">Adresse</label>
              <input
                id="adress"
                type="text"
                name="adress"
                value={editedCoach.adress || ""}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-field">
              <label htmlFor="comment">Notiz</label>
              <input
                id="comment"
                type="text"
                name="comment"
                value={editedCoach.comment || ""}
                onChange={handleEditChange}
              />
            </div>
            {/* Weitere Eingabefelder bleiben unverändert */}
            <div className="form-actions">
              <button className="btn-primary" onClick={handleSave}>
                💾 Speichern
              </button>
            </div>

            {/* Login History Button - only show if coach has linked user account */}
            {getLinkedUser() && (
              <div className="login-history-section">
                <button
                  className="btn-secondary"
                  onClick={() => setShowLoginHistory(true)}
                  style={{ width: "100%", marginTop: "10px" }}
                >
                  📜 Login-Historie anzeigen
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Login History Modal */}
      {showLoginHistory && getLinkedUser() && (
        <LoginHistoryModal
          userId={getLinkedUser()._id}
          userName={`${editedCoach.firstName} ${editedCoach.lastName}`}
          onClose={() => setShowLoginHistory(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div
          className="modal-overlay"
          onClick={() => setDeleteConfirmation(null)}
        >
          <div
            className="manage-student-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Trainer löschen</h3>
            <p className="student-name">
              <strong>
                Trainer {deleteConfirmation.coach.firstName}{" "}
                {deleteConfirmation.coach.lastName}
              </strong>
            </p>
            <p style={{ marginBottom: "1.5rem" }}>
              {deleteConfirmation.studentCount > 0 ? (
                <>
                  hat noch {deleteConfirmation.studentCount} zugewiesene
                  {deleteConfirmation.studentCount === 1 ? "n" : ""} Schüler.
                  Trotzdem löschen? Schüler ohne weiteren Slot werden auf die
                  "nicht zugewiesen" Liste verschoben.
                </>
              ) : (
                <>wirklich löschen?</>
              )}
            </p>
            <div className="dialog-buttons">
              <button className="btn-remove-all" onClick={confirmDelete}>
                Löschen
              </button>
              <button
                className="btn-cancel"
                onClick={() => setDeleteConfirmation(null)}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachManagement;
